import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { cssRef as _cssRef, mergeExternalProps as _mergeExternalProps, runtimeKeyframes as _runtimeKeyframes, runtimeCss as _runtimeCss } from "@terraindigital/static-styles-react";
const _staticStyles = _cssRef("drawer__el-0tlhui", {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0, 0, 0, 0.1)",
  zIndex: 2000,
  "&[data-state=\"open\"]": {
    animation: "static-styles-drawer-kf-062dsniz 250ms forwards"
  },
  "&[data-state=\"closed\"]": {
    animation: "static-styles-drawer-kf-2t41756o 350ms forwards"
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none!important"
  }
});
import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { CSSObject } from "@terraindigital/static-styles-react";
import { ModalCloseButton } from '../modal';
import usePartialAtBreakpoint from '../grid/use-partial-at-breakpoint';
import { borderRadius } from '../tokens';
const fadeIn = "static-styles-drawer-kf-062dsniz";
const fadeOut = "static-styles-drawer-kf-2t41756o";

/**
 * Dialog overlay — must use {@link React.forwardRef} so Radix `Dialog.Portal` / `Slot` can attach refs.
 */
const DrawerOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>((props, ref) => <DialogPrimitive.Overlay {..._mergeExternalProps({
  ref: ref
}, props, {
  css: _staticStyles
})} />);
DrawerOverlay.displayName = 'DrawerOverlay';
type PlacementPosition = 'top' | 'bottom' | 'left' | 'right';
interface DrawerContentProps {
  /**
   *  * The content of the drawer in JSX
   *  */
  children: React.ReactNode | React.ReactNode[];
  /**
   *  * This can either be an explicit className or
   *  * classNames generated as a result of using css prop
   *  * i.e. css={{width: "100%", height: "auto"}}
   *  *
   *  * You can use css prop to style the drawer content. e.g. if you want to change
   *  * the background color from white to any other color
   *  */
  className?: string;
  /**
   *  * Positioning of the drawer. The default value is left
   *  */
  placement: PlacementPosition | {
    [key: string]: PlacementPosition;
  };
  /**
   *  * The size of the drawer. For horizontally positioned drawers (i.e. left, right), this will affect the width.
   *  * For vertically positioned drawers, this will affect the height(i.e. top, bottom). THe default value is xs
   *  */
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  container?: HTMLElement;
  animation?: 'slide' | 'fade';
}
interface DrawerCloseProps {
  /**
   *  * The content of the drawer in JSX
   *  */
  children: React.ReactNode | React.ReactNode[];
}

/**
 * <DrawerContent /> Component. It has a white background by default.
 * It must be a direct child of the <Drawer /> component
 * You can
 * @param {CustomProps<DrawerContentProps>}
 * @returns {React.ReactElement}
 *
 *
 *
 *
 */

export const DrawerCloseOnClick: React.FunctionComponent<DrawerCloseProps> = ({
  children
}: DrawerCloseProps) => <DialogPrimitive.Close asChild>{children}</DialogPrimitive.Close>;
const buildInAnimation = (animation: CSSObject): string => `${_runtimeKeyframes(animation)} 360ms forwards`;
const buildOutAnimation = (animation: CSSObject): string => `${_runtimeKeyframes(animation)} 250ms forwards`;
export const DrawerContent = ({
  children,
  className,
  placement = 'bottom',
  size = 'xs',
  container,
  animation = 'slide'
}: CustomProps<DrawerContentProps>): React.ReactElement => {
  const sizes = {
    xs: '320px',
    sm: '384px',
    md: '448px',
    lg: '512px',
    xl: '576px',
    full: '100%'
  }[size];
  const placementStyles = usePartialAtBreakpoint(placement, {
    top: {
      transform: animation === 'slide' ? 'translate3d(0,-100%,0)' : undefined,
      width: '100%',
      height: sizes,
      top: 0,
      bottom: 'auto',
      borderBottomLeftRadius: borderRadius.small,
      borderBottomRightRadius: borderRadius.small
    },
    right: {
      top: '0',
      transform: animation === 'slide' ? 'translate3d(100%,0,0)' : undefined,
      right: 0,
      width: sizes,
      height: '100%',
      borderTopLeftRadius: borderRadius.small,
      borderBottomLeftRadius: borderRadius.small
    },
    bottom: {
      transform: animation === 'slide' ? 'translate3d(0,100%,0)' : undefined,
      width: '100%',
      height: sizes,
      bottom: 0,
      top: 'auto',
      borderTopLeftRadius: borderRadius.small,
      borderTopRightRadius: borderRadius.small
    },
    left: {
      top: 0,
      transform: animation === 'slide' ? 'translate3d(0%,0,0)' : undefined,
      left: 0,
      width: sizes,
      height: '100%',
      borderTopRightRadius: borderRadius.small,
      borderBottomRightRadius: borderRadius.small
    }
  });
  const animations = {
    slide: {
      top: {
        in: {
          from: {
            transform: 'translate3d(0,-100%,0)'
          },
          to: {
            transform: 'translate3d(0,0,0)'
          }
        },
        out: {
          from: {
            transform: 'translate3d(0,0,0)'
          },
          to: {
            transform: 'translate3d(0,-100%,0)'
          }
        }
      },
      right: {
        in: {
          from: {
            transform: 'translate3d(100%,0,0)'
          },
          to: {
            transform: 'translate3d(0,0,0)'
          }
        },
        out: {
          from: {
            transform: 'translate3d(0,0,0)'
          },
          to: {
            transform: 'translate3d(100%,0,0)'
          }
        }
      },
      bottom: {
        in: {
          from: {
            transform: 'translate3d(0,100%,0)'
          },
          to: {
            transform: 'translate3d(0,0,0)'
          }
        },
        out: {
          from: {
            transform: 'translate3d(0,0,0)'
          },
          to: {
            transform: 'translate3d(0,100%,0)'
          }
        }
      },
      left: {
        in: {
          from: {
            transform: 'translate3d(-100%,0,0)'
          },
          to: {
            transform: 'translate3d(0,0,0)'
          }
        },
        out: {
          from: {
            transform: 'translate3d(0,0,0)'
          },
          to: {
            transform: 'translate3d(-100%,0,0)'
          }
        }
      }
    },
    fade: {
      ...Object.fromEntries(['top', 'bottom', 'left', 'right'].map(key => [key, {
        in: {
          from: {
            opacity: '0'
          },
          to: {
            opacity: '1'
          }
        },
        out: {
          from: {
            opacity: '1'
          },
          to: {
            opacity: '0'
          }
        }
      }]))
    }
  }[animation];
  const animationStylesIn = usePartialAtBreakpoint(placement, animations, 'in');
  const animationStylesOut = usePartialAtBreakpoint(placement, animations, 'out');
  return <DialogPrimitive.Portal container={container}>
            <DrawerOverlay />
            <DialogPrimitive.Content {..._mergeExternalProps({
      css: _runtimeCss({
        backgroundColor: 'white',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        position: 'fixed',
        ...placementStyles,
        maxWidth: '100%',
        maxHeight: '100%',
        zIndex: 2000,
        '&[data-state="open"]': {
          // animation: `${slideIn} 360ms forwards`,
          ...Object.fromEntries(Object.entries(animationStylesIn).map(([query, animation]) => [query, {
            animation: buildInAnimation(animation)
          }]))
        },
        '&[data-state="closed"]': {
          ...Object.fromEntries(Object.entries(animationStylesOut).map(([query, animation]) => [query, {
            animation: buildOutAnimation(animation)
          }]))
          // animation: `${slideOut} 250ms forwards`,
        }
      }),
      className: className
    })}>
                {children}
            </DialogPrimitive.Content>
        </DialogPrimitive.Portal>;
};
type DialogRootPrimitiveProps = React.FunctionComponent<React.ComponentProps<typeof DialogPrimitive.Root>>;
type DialogTriggerPrimitiveProps = React.FunctionComponent<React.ComponentProps<typeof DialogPrimitive.Trigger>>;
type DrawerCloseButtonProps = React.FunctionComponent<React.ComponentProps<typeof ModalCloseButton>>;

/**
 * <Drawer /> component - This is the root component that houses
 * both the DrawerTrigger and DrawerContent component when creating a Drawer
 *
 */
export const DrawerTitle = DialogPrimitive.Title;
export const DrawerDescription = DialogPrimitive.Description;
export const Drawer: DialogRootPrimitiveProps = DialogPrimitive.Root;
/**
 * <DrawerTrigger /> component - This is the button that opens the Drawer when clicked
 * It must have a child that determines the look of the button
 * (i.e. text and styling), preferably a div element which can be styled using css prop.
 *
 * This button must be a direct child of the <Drawer /> component
 *
 */
export const DrawerTrigger: DialogTriggerPrimitiveProps = DialogPrimitive.Trigger;
/**
 * <DrawerCloseButton /> component.
 * This component must always be the first child of <DrawerContent /> component.
 * You can modify the style with css prop e.g. css={{width: "100px", height: "100px"}}
 */
export const DrawerCloseButton: DrawerCloseButtonProps = ModalCloseButton;